import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, FolderKanban, FileCheck2, ScrollText, Image as ImageIcon,
  Sparkles, BookOpen, LogOut, Search, Send, Upload, ShieldCheck, AlertTriangle,
  CheckCircle2, ChevronRight, Building2, Hash, BarChart3, MessageSquareText, Loader2, X,
  Inbox, Link as LinkIcon, Menu, LogIn, FileText
} from "lucide-react";
import { API } from "../lib/api";
import { fmtAUD, fmtMoney, fmtBytes } from "../lib/format";

/* ---------- Workflow presentation — EPAR Scope ---------- */
export function WorkflowNSWRABenefits() {
  const benefits = [
    {
      pillar: "Consistency at scale",
      tagline: "Every assessor, every council, same standard",
      body: "RAAgent enforces the published DRFA rules (Fact Sheet on-costs, CET v2.1 rates, cost-band thresholds, Cat B folder spec) on every claim — uniformly. No assessor-to-assessor drift. New assessors onboarded in days, not months.",
      tone: "bg-blue-50 border-blue-200 text-blue-900",
      dot: "bg-blue-500",
    },
    {
      pillar: "Audit-ready defensibility",
      tagline: "Every decision has a name + a reason + a timestamp",
      body: "Every assessor verdict (YES/RFI/NO, per-line eligibility override, on-cost toggle, free-text rationale) is captured to Mongo and flows into the downloaded artefacts as an audit trail. Survives ANAO/DRFA review. Councils get clear methodology back, not opaque rejections.",
      tone: "bg-emerald-50 border-emerald-200 text-emerald-900",
      dot: "bg-emerald-500",
    },
    {
      pillar: "Cost recovery for the State",
      tagline: "Catches systemic submission errors that currently flow through",
      body: "Auto-strips double-counted on-cost lines (a common council mistake worth low single-digit %s of the claim). Flags out-of-band PM/Design/Contingency. Reconciles submitted vs verified CET rates per line. P50 vs P90 separation prevents the State paying its own risk envelope.",
      tone: "bg-amber-50 border-amber-200 text-amber-900",
      dot: "bg-amber-500",
    },
    {
      pillar: "Throughput — clear the backlog",
      tagline: "From package open to signed assessment in a single session",
      body: "Ledger auto-loads from the package. Photos auto-bucket. CET workings cache instantly. AI extracts defects from PDF-only packages. The assessor spends time on judgement, not file-hunting — measurable throughput uplift per assessor per day.",
      tone: "bg-violet-50 border-violet-200 text-violet-900",
      dot: "bg-violet-500",
    },
    {
      pillar: "Knowledge stays with NSWRA",
      tagline: "Codified DRFA expertise, not stuck in one assessor's head",
      body: "Fact Sheet rates, Cat B specifications, on-cost rules, cost-band thresholds, P50/P90 policy — all version-controlled in the application. When NSWRA updates a rule, every assessor uses the new rule the same day. Institutional knowledge survives staff turnover.",
      tone: "bg-slate-100 border-slate-300 text-slate-900",
      dot: "bg-slate-700",
    },
    {
      pillar: "Tool, not deciders",
      tagline: "Respects the NSWRA principle — the human signs off",
      body: "The four tools (CET Verifier, Ledger Checker, Photo Checker, DRFA Knowledge Base) present evidence + three options (Accept / RFI / Reject). The assessor decides. RAAgent never approves or rejects autonomously. Defensible by design.",
      tone: "bg-rose-50 border-rose-200 text-rose-900",
      dot: "bg-rose-500",
    },
  ];
  return (
    <div className="card p-6 bg-gradient-to-br from-slate-900 to-slate-800 border-slate-900 text-white" data-testid="workflow-benefits">
      <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-emerald-400 mb-2">Why this matters to NSWRA</div>
      <h3 className="font-heading text-2xl font-bold mb-4">Six outcomes the agency gets from day one</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {benefits.map((b, i) => (
          <div key={i} className={`rounded-lg border p-4 ${b.tone}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`w-2 h-2 rounded-full ${b.dot}`}/>
              <div className="text-[10px] tracking-[0.14em] uppercase font-bold">{b.pillar}</div>
            </div>
            <div className="font-heading text-[15px] font-bold leading-tight mb-1.5">{b.tagline}</div>
            <p className="text-[12px] leading-relaxed opacity-90">{b.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}


export function WorkflowEPARScope() {
  const steps = [
    {
      n: 1, title: "Ingest the SmartyGrants package",
      sub: "ZIP upload or Google Drive link — multi-portal aware",
      body: "The assessor pastes a Drive link (or drags a ZIP) into the Ingest Inbox. RAAgent unpacks nested ZIPs in the background — 100+ MB / 500+ photo packages don't block the browser. The portal type is auto-detected (EW / IRW / EPAR Scope / EPAR Final) from filenames + PDF metadata with 95-100% confidence. All three SmartyGrants spreadsheet flavours route through the same flexible parser; if the package is PDF-only, one-click AI extraction is available.",
      tech: "Background tasks · nested ZIP auto-unpack · multi-portal SG parser (EW / IRW / EPAR) · async ingest · disk auto-pruning · AI PDF fallback (Claude Sonnet 4.5)",
    },
    {
      n: 2, title: "Sortable ingested file tree — every artefact at a click",
      sub: "Ledger, CET workbooks, photos, SRA, COC — all surfaced in-app",
      body: "Every file in the package surfaces in a sortable, filterable file tree. Filter chips: Claim forms · CET workbooks · Slip estimates · Ledgers · Photos · Evidence ZIPs · PDFs. Sort by name / classification / size / type. Search any path. Open any file inline (PDFs, images) or download (Excel, ZIP). The tree is snapshotted to Mongo so it survives backend reboots and disk pruning — assessors never need to hunt outside the app.",
      tech: "files_seen classifier · /source-tree + /source-file endpoints · path-traversal-safe streaming · Mongo snapshot for disk-pruning resilience",
    },
    {
      n: 3, title: "Per-DM evidence review",
      sub: "Photos, CET, slip estimate — drawer-style per Damage Memo",
      body: "Click any DM row to expand its evidence drawer: pre-event + post-event photo strips with lightbox, verifier flags per photo (EXIF / GPS / resolution), the slip construction estimate alongside the CET, and Form 306 claim items mentioning that DM. The package layout is rendered against the NSWRA Cat B file-structure spec — missing folders show as 'empty'. A self-healing photo re-walk fires automatically if photos are added later or the classifier improves.",
      tech: "Per-DM photo classification · NSWRA Cat B spec compliance · click-to-inspect EXIF/GPS/resolution · self-healing re-walk on first job open",
    },
    {
      n: 4, title: "CET workings — submitted vs verified, in <1s",
      sub: "Click the dollar value, get the full line-by-line breakdown",
      body: "Per DM, click the project value to open the CET workings modal — every line item verified against the published CET v2.1 schedule (60 ref codes across URT / SRR / CE / CW / DS / PW / RFD), cost-band check on PM / Design / Contingency / Escalation, and a P50 vs P90 panel explaining the policy (P50 = claimable, P90 = State-level risk envelope, NOT claimable). The first parse runs in a worker thread so the loop stays responsive; results persist to Mongo so repeat opens (even after backend reboot) return in <200ms.",
      tech: "60-ref CET v2.1 schedule · cost-band thresholds per NSWRA · asyncio.to_thread parse · Mongo write-through cache (cold 2.9s → warm 137ms) · P50/P90 spread analysis",
    },
    {
      n: 5, title: "Tools, not deciders — YES / RFI / NO",
      sub: "Per the NSWRA principle: tool presents options, human decides",
      body: "Each DM row has YES / RFI / NO verdict buttons plus a free-text 'method' field that flows to Form 2 column H of the SQP Assessment Record. An Auto-fill option drafts the method text from the CET Verifier + Photo Checker findings — the assessor edits before saving. Standalone tool versions (CET Verifier, Photo Checker, Ledger Checker — with per-row override controls and bulk actions) are exposed publicly so anyone can sanity-check a single workbook outside a full assessment.",
      tech: "Per-DM decision persistence · Form 2 G + H write-back via openpyxl · LLM-assisted method drafting · 3 public tools with assessor override + audit-trail download",
    },
    {
      n: 6, title: "Verified Assessment Package output",
      sub: "Folder structure matches the NSWRA Cat B spec exactly",
      body: "When satisfied, RAAgent emits the Verified Package ZIP in the exact folder structure NSWRA expects: Documents / Cat B / <SG_ID> / Application Assessment / sub-folders for Assessment Form, Assessor Correspondence, Verified vs Non-Verified Cost Estimation Evidence, Verified Damage Evidence (per DM, with 01 Pre-Disaster / 02 Verified / 03 Non-Verified), Verified Supporting Evidence. Files route based on the assessor's YES/RFI/NO decisions. The package tree itself is snapshotted to Mongo so assessors can still preview it after disk pruning.",
      tech: "Decision-routed file placement · YES/RFI/NO bucketing per DM · Cat B spec compliance · Mongo snapshot for post-pruning preview · ready for SharePoint sync",
    },
  ];
  return (
    <div className="space-y-8" data-testid="workflow-epar-scope-page">
      <div className="border-b border-slate-200 pb-6">
        <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-blue-700 mb-2">Workflow walkthrough</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">EPAR Scope Approval</h1>
        <p className="text-base text-slate-600 mt-2 max-w-3xl leading-relaxed">
          How RAAgent guides an NSWRA assessor from a raw SmartyGrants package to a signed,
          verified assessment package — in six steps, with the assessor making every decision.
        </p>
        <div className="mt-4 flex flex-wrap gap-3 text-[12px]">
          <Link to="/portal/EPAR%20Scope%20Approval" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-700" data-testid="workflow-cta-portal">
            <FolderKanban className="w-3.5 h-3.5"/> Open EPAR Scope portal
          </Link>
          <Link to="/tools/cet-verifier" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
            <BarChart3 className="w-3.5 h-3.5"/> Try the CET Verifier
          </Link>
          <Link to="/tools/photo-checker" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
            <ImageIcon className="w-3.5 h-3.5"/> Try the Photo Checker
          </Link>
          <Link to="/tools/ledger-checker" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
            <ScrollText className="w-3.5 h-3.5"/> Try the Ledger Checker
          </Link>
        </div>
      </div>

      <WorkflowNSWRABenefits />

      <div className="space-y-5">
        {steps.map((s) => (
          <div key={s.n} className="card p-6 flex gap-5" data-testid={`workflow-step-${s.n}`}>
            <div className="shrink-0 w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center font-heading font-bold text-2xl">
              {s.n}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 mb-0.5">Step {s.n}</div>
              <h2 className="font-heading text-xl font-semibold leading-tight">{s.title}</h2>
              <div className="text-sm text-slate-500 italic mt-0.5">{s.sub}</div>
              <p className="text-[13.5px] text-slate-700 mt-3 leading-relaxed">{s.body}</p>
              <div className="mt-3 text-[11px] text-slate-500 font-mono border-l-2 border-slate-200 pl-3">
                {s.tech}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card p-6 bg-gradient-to-br from-emerald-700 to-teal-800 border-emerald-900 text-white">
        <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-emerald-300 mb-2">Bottom line for NSWRA</div>
        <h3 className="font-heading text-2xl font-bold mb-3">A defensible, scalable, tool-not-decider assessment platform — built around how NSWRA already works</h3>
        <ul className="text-[13.5px] space-y-2 list-disc pl-5 opacity-95">
          <li><span className="font-semibold">More claims per assessor per week</span> — the file-hunting and re-keying time disappears. Judgement time is preserved.</li>
          <li><span className="font-semibold">Audit-defensible</span> — every override, every verdict, every methodology note captured with a name + timestamp + reason. ANAO and DRFA reviewers can trace every dollar.</li>
          <li><span className="font-semibold">Catches submission errors the agency currently absorbs</span> — double-counted on-costs, out-of-band PM/Design, P50/P90 confusion, off-schedule CET rates.</li>
          <li><span className="font-semibold">DRFA expertise codified, not personified</span> — rate updates, threshold changes, Cat B spec updates all flow to every assessor the same day.</li>
          <li><span className="font-semibold">Council-facing transparency</span> — clear methodology back to councils means fewer RFI loops and better submissions next time.</li>
          <li><span className="font-semibold">The assessor signs off, always</span> — RAAgent never decides. It presents evidence and three options. Defensible by design.</li>
        </ul>
      </div>

      <div className="card p-6 bg-slate-50 border-slate-200">
        <h3 className="font-heading text-lg font-semibold mb-2">What this saves the assessor</h3>
        <ul className="text-[13px] text-slate-700 space-y-1.5 list-disc pl-5">
          <li><span className="font-semibold">No manual file sorting</span> — RAAgent unpacks and classifies hundreds of files into the expected NSWRA structure automatically.</li>
          <li><span className="font-semibold">No spreadsheet hunting</span> — CET line-by-line verification + cost-band check + P50/P90 reconciliation appear in one click on each DM&apos;s dollar value.</li>
          <li><span className="font-semibold">No policy ambiguity</span> — every component has a plain-English explainer (Why P50, what is contingency, escalation rules) so the assessor and the council are on the same page.</li>
          <li><span className="font-semibold">No assembly time at the end</span> — the Verified Package ZIP comes out in the exact NSWRA file structure, ready to drop into SharePoint.</li>
          <li><span className="font-semibold">The decision stays human</span> — RAAgent never approves or rejects on its own. It surfaces evidence and presents the three options. The assessor signs off.</li>
        </ul>
      </div>
    </div>
  );
}
